import { Box, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShepherdTour } from 'react-shepherd';
import { ScrollableButtonTabs, Spinner, WsColors } from 'wisestep-base';

import { WsBreadCrumbs } from '../../components/WsBreadCrumbs';
import { tourOptions, jobcrmSteps } from '../../utils/TourStepsAndOptions';
import AllCommunicationsLog from '../conversationWidget/components/CommunicationsLog';
import AcceptJob from '../jobs/listing/components/acceptJob';
import JobPageParams from '../jobs/types/JobPageParams';
import UserProfileOverview from '../recruiterProfileDetails/components/userProfile/UserProfileOverview';
import { TaskDefaultQuery } from '../tasks/TaskDefaultQuery';

import JobActivityLog from './activityLogJob/JobsActivityLog';
import { JobAllocation } from './allocation';
import {
  getJobCrmBreadCrumbList,
  JobsCrm,
  jobsCrmRouteNames,
  jobsCrmTabNames,
} from './components/helper/JobsCrmHelper';
import { useCandidateStats } from './components/helper/JobsStatsHook';
import { JobCRMHeader } from './components/jobCrmHeader';
import { useJobCRMContext } from './context';
import { JobDetails } from './jobDetails';
import JobsCrmPipeline from './pipeline';
import JobCrmReminders from './reminders';
import { Sourcing } from './sourcing';

const JobsCrmListing = () => {
  const params = useParams<JobPageParams>();
  const navigate = useNavigate();
  const jobId = params.jobId ?? '';
  const encodedEmail = params.encodedEmail ?? '';

  const { workAssignment, job, loadingJobInfo } = useJobCRMContext();
  const jobStats = useCandidateStats(jobId);
  const [pageType, setPageType] = useState(params.type ?? JobsCrm.JOB_DETAILS);
  const jobsCrmType = {
    [JobsCrm.PIPELINE]: () => {
      setPageType(JobsCrm.PIPELINE);
      !isEmpty(encodedEmail)
        ? window.history.pushState(null, ``, `/jobs-crm/${jobId}/false/0/pipeline/${encodedEmail}`)
        : window.history.pushState(null, ``, `/jobs-crm/${jobId}/false/0/pipeline`);
    },
    [JobsCrm.TASKS]: () => {
      setPageType(JobsCrm.TASKS);
      window.history.pushState(null, ``, `/jobs-crm/${jobId}/false/0/tasks`);
    },
    [JobsCrm.JOB_ANALYTICS]: () => {
      setPageType(JobsCrm.JOB_ANALYTICS);
      window.history.pushState(null, ``, `/jobs-crm/${jobId}/job-analytics`);
    },
    [JobsCrm.ALL_COMMUNICATIONS]: () => {
      setPageType(JobsCrm.ALL_COMMUNICATIONS);
      window.history.pushState(null, ``, `/jobs-crm/${jobId}/all-communications`);
    },
    [JobsCrm.JOB_DETAILS]: () => {
      setPageType(JobsCrm.JOB_DETAILS);
      navigate(`/jobs-crm/${jobId}/false/0/jobDetails`);
    },
    [JobsCrm.ALLOCATION]: () => {
      setPageType(JobsCrm.ALLOCATION);
      navigate(`/jobs-crm/${jobId}/false/0/allocation`);
    },
    [JobsCrm.SOURCING]: () => {
      setPageType(JobsCrm.SOURCING);
      navigate(`/jobs-crm/${jobId}/false/0/sourcing`);
    },
    [JobsCrm.ACTIVITY_LOGS]: () => {
      setPageType(JobsCrm.ACTIVITY_LOGS);
      window.history.pushState(null, ``, `/jobs-crm/${jobId}/false/0/activitylogs`);
    },
  };

  const selectIndex = (index: number) => {
    jobsCrmType[jobsCrmRouteNames[index]]?.();
  };

  return (
    <>
      {loadingJobInfo && <Spinner />}
      {job && !loadingJobInfo && (
        <Box>
          <ShepherdTour steps={jobcrmSteps} tourOptions={tourOptions}>
            <WsBreadCrumbs breadCrumbsList={getJobCrmBreadCrumbList(job?.title ?? 'Job Title')} />
            <JobCRMHeader />
            <Grid item mt={1}>
              {!job?.isClosedForAssigningRecruiters && !workAssignment?.isAccepted && (
                <AcceptJob job={job} />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              borderRadius={2}
              mt={1}
              sx={{
                background: WsColors.White,
              }}
            >
              <ScrollableButtonTabs
                scrollList={jobsCrmTabNames}
                getTabIndex={selectIndex}
                defaultValue={jobsCrmRouteNames.findIndex((val) => val === pageType) ?? 0}
              />
            </Grid>
            {pageType === JobsCrm.PIPELINE && (
              <JobsCrmPipeline
                job={job}
                candidateEmail={encodedEmail}
                index={Number(params.defaultJDTab)}
                jobStats={jobStats[0]}
              />
            )}
            {pageType === JobsCrm.SOURCING && <Sourcing job={job} />}
            {pageType === JobsCrm.JOB_DETAILS && <JobDetails job={job} />}
            {pageType === JobsCrm.TASKS && (
              <JobCrmReminders
                customQuery={{
                  ...TaskDefaultQuery,
                  taskAttributes: { jobCode: [job.jobCode ?? ''] },
                }}
              />
            )}
            {pageType === JobsCrm.JOB_ANALYTICS && (
              <UserProfileOverview jobId={job.id} jobPostedOn={job.createdOn} />
            )}
            {pageType === JobsCrm.ALL_COMMUNICATIONS && (
              <Box
                mt={1.5}
                sx={{
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
                  bgcolor: WsColors.White,
                  borderRadius: 1.5,
                  padding: 1,
                  overflow: 'hidden',
                }}
              >
                <AllCommunicationsLog entityId={jobId} type="JOB" />
              </Box>
            )}
            {pageType === JobsCrm.ALLOCATION && <JobAllocation />}
            {pageType === JobsCrm.ACTIVITY_LOGS && <JobActivityLog jobId={jobId} />}
          </ShepherdTour>
        </Box>
      )}
    </>
  );
};
export default JobsCrmListing;
